'use client';

import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { analytics } from 'lib/analytics/analytics';
import { AppleLogo } from 'assets/icons/AppleLogo';
import { PrimaryButton } from 'components/shared/PrimaryButton';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { ToastContext } from 'components/shared/Toast';
import { addToCartArgsType } from 'components/shared/Cart/types';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  APPLE_PAY_API_VERSION,
  STORE_TO_COUNTRY,
  STORE_TO_CURRENCY,
  isApplePayEnabled,
  isApplePaySupportedStore,
} from 'lib/commerce/applePay/applePayHelper';
import {
  useApplePayStripe,
  ExpressDisplayItem,
} from 'hooks/commerce/useApplePayStripe';
import styles from './ApplePayExpressButton.module.css';

const MERCHANT_LABEL = 'Ultrahuman';

const toCartLines = (items: addToCartArgsType[]) =>
  items
    .filter((item) => item.shopifyVariantId)
    .map((item) => ({
      merchandiseId: item.shopifyVariantId as string,
      quantity: 1,
      attributes: (item.params?.noteAttributes ?? []).map((attr) => ({
        key: attr.key,
        value: attr.value,
      })),
    }));

export const ApplePayExpressButton = ({
  className,
  totalAmount,
  getLineItems,
  ringColor,
  product,
  onCheckoutBlocked,
}: {
  className?: string;
  totalAmount: number;
  getLineItems: () => addToCartArgsType[];
  // Cosmetic context for the pending thank-you page (hero image + caption).
  ringColor?: string;
  product: string;
  onCheckoutBlocked?: () => void;
}) => {
  const t = useTranslations('ring');
  const router = useRouter();
  const CartClient = useCartStore(
    useShallow((s) => ({
      store: s.store,
      getExpressCartInput: s.getExpressCartInput,
    })),
  );
  const { showToast } = useContext(ToastContext);

  const store = (CartClient.store ?? undefined) as ShopifyStore | undefined;
  const countryCode = store ? STORE_TO_COUNTRY[store] : undefined;
  const currencyCode = store ? STORE_TO_CURRENCY[store] : undefined;
  const ariaLabel = t('ring.productSelection.cartSummary.buyWithApplePayAria');

  const displayLineItems = useMemo<ExpressDisplayItem[]>(
    () => [{ label: 'Subtotal', amount: totalAmount.toFixed(2) }],
    [totalAmount],
  );

  // Store methods are recreated each render; refs keep the hook callbacks stable.
  const getLineItemsRef = useRef(getLineItems);
  getLineItemsRef.current = getLineItems;
  const getExpressCartInputRef = useRef(CartClient.getExpressCartInput);
  getExpressCartInputRef.current = CartClient.getExpressCartInput;

  const cartCacheRef = useRef<{ key: string; cartId: string } | null>(null);

  const createCart = useCallback(async (): Promise<string | null> => {
    if (!store) return null;
    const items = getLineItemsRef.current?.() ?? [];
    const lines = toCartLines(items);
    if (lines.length === 0) return null;

    const { attributes, discountCodes } = getExpressCartInputRef.current?.() ?? {
      attributes: [],
      discountCodes: [],
    };
    const allDiscountCodes = [
      ...discountCodes,
      ...items
        .map((item) => item.discountCode)
        .filter((code): code is string => !!code),
    ];

    const key = JSON.stringify([store, countryCode, lines, allDiscountCodes, attributes]);
    if (cartCacheRef.current?.key === key) return cartCacheRef.current.cartId;

    const resp = await fetch(
      `/api/vendor/shopify/cart/create/?store=${store}&apiVersion=${APPLE_PAY_API_VERSION}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lines,
          attributes,
          discountCodes: allDiscountCodes,
          countryCode,
        }),
      },
    );
    const data = await resp.json();
    const cartId = data?.cart?.id;
    if (!resp.ok || !cartId) return null;
    cartCacheRef.current = { key, cartId };
    return cartId;
  }, [store, countryCode]);

  const { startSession, status, canUseApplePay, ready } = useApplePayStripe({
    store,
    countryCode,
    currencyCode,
    totalAmount,
    label: MERCHANT_LABEL,
    displayLineItems,
    createCart,
    onPaid: (paid) => {
      // The consumed cart must not be reused on a repeat purchase.
      cartCacheRef.current = null;
      if (paid?.intentToken) {
        const model = product.includes('PRO') ? 'pro' : 'air';
        const color = ringColor ? `&color=${encodeURIComponent(ringColor)}` : '';
        router.push(
          `/order/ring/${encodeURIComponent(paid.intentToken)}/?type=intent&model=${model}${color}`,
        );
        return;
      }
      // Fallback: the poll/onSuccess path takes over.
      showToast('Payment successful — setting up your order…', 'success', 8000);
    },
    onSuccess: (confirmationUrl) => {
      if (confirmationUrl) {
        window.location.href = confirmationUrl;
        return;
      }
      showToast(
        'Payment received. Your order confirmation will arrive by email shortly.',
        'success',
        8000,
      );
    },
    onFailure: (reason) => {
      console.warn('Apple Pay express failed:', reason);
      const message =
        reason === 'network'
          ? 'We couldn’t confirm your payment. Please check your email for a receipt before trying again.'
          : reason === 'price_changed'
            ? 'The price has changed since this page loaded. Please refresh and try again — you have not been charged.'
            : reason === 'order_failed'
              ? 'Something went wrong setting up your order. Your payment is being refunded automatically.'
              : 'Payment didn’t complete. You have not been charged — please try again.';
      showToast(message, 'error', 8000);
    },
  });

  const enabled = isApplePayEnabled();
  const storeSupported = isApplePaySupportedStore(store);
  const willRender =
    enabled &&
    storeSupported &&
    !!countryCode &&
    !!currencyCode &&
    totalAmount > 0 &&
    canUseApplePay;

  useEffect(() => {
    if (willRender) {
      analytics.track('applepay_button_shown', {
        store,
        currency: currencyCode,
        amount: totalAmount,
        product,
      });
    }
  }, [willRender, store, currencyCode, totalAmount, product]);

  if (!willRender) return null;

  const handleClick = () => {
    if (onCheckoutBlocked) {
      onCheckoutBlocked();
      return;
    }
    startSession();
  };

  return (
    <PrimaryButton
      type="button"
      className={[styles.applePayButton, className].filter(Boolean).join(' ')}
      onClick={handleClick}
      aria-label={ariaLabel}
      aria-busy={status === 'authorizing'}
      disabled={!ready || status === 'authorizing'}
      data-buttontype="apple pay"
      data-product={product}
    >
      <span className={styles.label}>
        {t.rich('ring.productSelection.cartSummary.buyWithApplePay', {
          appleLogo: () => (
            <span className={styles.logoSlot} aria-hidden="true">
              <AppleLogo className={styles.logo} fill="#ffffff" />
            </span>
          ),
        })}
      </span>
    </PrimaryButton>
  );
};
